/**
 * Configuration modales objectifs atelier (par nom de jalon ou action workflow).
 */

import {
  getMilestoneDisplaySubtitle,
  isPrintingOutcomeMilestone,
  PRINTING_OUTCOME_OPTIONS,
} from '../../lib/milestoneOutcomeUtils';

export const MILESTONE_MODAL_TYPES = {
  GENERIC: 'generic',
  PRODUCTION_SHEET: 'production_sheet',
  IPR: 'ipr',
  CHECKLIST: 'checklist',
  MODE_CHOICE: 'mode_choice',
};

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** Icône Lucide selon le libellé du jalon. */
export function getMilestoneIcon(name) {
  const n = normalize(name);
  if (n.includes('start') || n.includes('debut') || n.includes('début')) return 'Play';
  if (n.includes('etat') || n.includes('état') || n.includes('status')) return 'Activity';
  if (n.includes('termine') || n.includes('terminé') || n.includes('finished')) return 'CheckCircle2';
  if (n.includes('laser') || n.includes('manuel')) return 'Scissors';
  if (n.includes('stripping') || n.includes('ipr')) return 'Layers';
  if (n.includes('etiquette') || n.includes('étiquette') || n.includes('label')) return 'Tag';
  if (n.includes('packaging') || n.includes('colis')) return 'Package';
  if (n.includes('expedition') || n.includes('expédition') || n.includes('validation')) return 'Truck';
  if (n.includes('qualit') || n.includes('contrôle') || n.includes('controle')) return 'ShieldCheck';
  if (n.includes('impression') && n.includes('3')) return 'Printer';
  if (n.includes('thermo')) return 'Flame';
  return 'CircleDot';
}

/**
 * Type de modale pour un jalon (actions workflow prioritaires).
 */
export function resolveMilestoneModalType(milestone, templateActions = []) {
  const keys = templateActions.map((a) => a.action_key);
  if (keys.includes('open_production_sheet')) return MILESTONE_MODAL_TYPES.PRODUCTION_SHEET;
  if (keys.includes('open_ipr_milestone')) return MILESTONE_MODAL_TYPES.IPR;

  const n = normalize(milestone?.name);

  if (n.includes('stripping') || n.includes('fiche de stripping')) return MILESTONE_MODAL_TYPES.IPR;
  if (n.includes('laser') && n.includes('manuel')) return MILESTONE_MODAL_TYPES.MODE_CHOICE;
  if (
    n.includes('controle') ||
    n.includes('contrôle') ||
    n.includes('qualit') ||
    n.includes('packaging') ||
    n.includes('bouton')
  ) {
    return MILESTONE_MODAL_TYPES.CHECKLIST;
  }

  return MILESTONE_MODAL_TYPES.GENERIC;
}

/** Champs optionnels affichés dans la modale générique. */
export function getGenericModalFields(milestone) {
  const n = normalize(milestone?.name);
  const fields = [{ key: 'note', type: 'textarea', label: 'Note', placeholder: 'Commentaire…' }];

  if (isPrintingOutcomeMilestone(milestone?.name)) {
    fields.unshift({
      key: 'outcomeChoice',
      type: 'segment',
      label: 'Résultat',
      options: PRINTING_OUTCOME_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        segmentClass: o.segmentClass,
      })),
    });
    return fields;
  }

  if (n.includes('start') || n.includes('debut')) {
    fields.unshift({ key: 'startedAt', type: 'datetime-local', label: 'Heure de début' });
  }
  if (n.includes('termine') || n.includes('terminé')) {
    fields.unshift({ key: 'endedAt', type: 'datetime-local', label: 'Heure de fin' });
  }
  if ((n.includes('etat') || n.includes('état')) && !isPrintingOutcomeMilestone(milestone?.name)) {
    fields.unshift({
      key: 'statusChoice',
      type: 'segment',
      label: 'État',
      options: [
        { value: 'en_cours', label: 'En cours' },
        { value: 'pause', label: 'Pause' },
        { value: 'incident', label: 'Incident' },
      ],
    });
  }
  if (n.includes('thermo') && n.includes('termine')) {
    fields.unshift({
      key: 'visualOk',
      type: 'segment',
      label: 'Contrôle visuel',
      options: [
        { value: 'oui', label: 'OK' },
        { value: 'non', label: 'Non conforme' },
      ],
    });
  }

  return fields;
}

/** Points checklist pour modales QC / finition. */
export function getChecklistItems(milestone) {
  const n = normalize(milestone?.name);
  if (n.includes('conception') && n.includes('modele')) {
    return ['Modèle conforme au plan', 'Numérotation aligneurs OK', 'Pas de défaut visible'];
  }
  if (n.includes('decoupe') || n.includes('découpe')) {
    return ['Découpe nette', 'Bords polis', 'Alignement étiquettes'];
  }
  if (n.includes('qualit') && n.includes('expedition')) {
    return ['Emballage intact', 'Contenu conforme fiche', 'Étiquette colis OK'];
  }
  if (n.includes('packaging')) {
    return ['Colis préparé', 'Accessoires inclus', 'Étiquette apposée'];
  }
  if (n.includes('bouton') || n.includes('cut')) {
    return ['Boutons découpés', 'Cut-outs réalisés'];
  }
  if (n.includes('etiquette') || n.includes('étiquette')) {
    return ['Étiquettes imprimées', 'Codes lisibles'];
  }
  return ['Contrôle effectué', 'Conforme aux spécifications'];
}

export function getMilestoneStatusLabel(milestone, phaseStatus, milestoneState) {
  if (milestone?.completed === true) {
    return getMilestoneDisplaySubtitle(milestone, phaseStatus);
  }
  const isRejected =
    milestone?.is_rejected === true || milestone?.rejected_date != null;
  if (isRejected) {
    return getMilestoneDisplaySubtitle(milestone, phaseStatus);
  }
  if (milestoneState === 'locked' || phaseStatus !== 'in_progress') {
    return 'En attente';
  }
  if (milestoneState === 'active' && phaseStatus === 'in_progress') {
    return 'À faire';
  }
  return getMilestoneDisplaySubtitle(milestone, phaseStatus);
}

/**
 * Ordre séquentiel : le premier jalon incomplet est « actif », les suivants sont verrouillés.
 */
export function partitionMilestonesByOrder(milestones) {
  const sorted = [...(milestones || [])].sort(
    (a, b) => (a.milestone_order ?? 0) - (b.milestone_order ?? 0)
  );
  const firstIncompleteIdx = sorted.findIndex((m) => !m.completed);
  const activeIndex = firstIncompleteIdx === -1 ? sorted.length : firstIncompleteIdx;

  return sorted.map((m, i) => ({
    milestone: m,
    state: m.completed ? 'done' : i === activeIndex ? 'active' : 'locked',
  }));
}
